import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ensureNamespaces } from '../../i18n';
import { consumeLandingSectionScroll, scrollToLandingSection } from '../../utils/landingAnchorScroll';
import WelcomeSlider from '../welcomePage/welcomeSlider';
import {
  LANDING_EXPORT_IMAGE,
  LANDING_FEATURE_SCREENS,
  LANDING_HERO_IMAGE,
} from './landingPageImages';
import { useLandingImagePreload } from './useLandingImagePreload';
import appStoreBadge from '../../images/landing/appstore_button.svg.webp';
import googlePlayBadge from '../../images/landing/google_play_button.svg.webp';
import { ANDROID_PLAY_STORE_URL, IOS_APP_STORE_URL } from '../../utils/storeUrls';

import {
  ExportRecordsContent,
  ExportRecordsImage,
  ExportRecordsRow,
  ExportRecordsSection,
  ExportRecordsSubtitle,
  ExportRecordsTitle,
  ExportRecordsVisual,
  FeatureCareInner,
  FeatureCareSection,
  FeatureCareSliderWrap,
  FeatureCareTitle,
  FeatureScreenCaption,
  FeatureScreenCard,
  FeatureScreenImage,
  FeatureScreenImageButton,
  FeatureScreenImageWrap,
  FeatureScreensGrid,
  FeatureScreensSection,
  FeatureScreensTitle,
  HeroContent,
  HeroDescription,
  HeroImage,
  HeroSection,
  HeroTitle,
  HeroVisual,
  LandingContainer,
  LandingPageRoot,
  StoreBadge,
  StoreBadgeLink,
  StoreBadgesRow,
  StoreButtons,
} from './landingPage.styles';
import LandingFaqSection from './landingFaqSection';
import LandingContactsSection from './landingContactsSection';
import LandingFooterSection from './landingFooterSection';
import LandingHeaderSection from './landingHeaderSection';
import LandingFeatureScreenLightbox from './landingFeatureScreenLightbox';

const FEATURE_SCREENS = LANDING_FEATURE_SCREENS;

export default function LandingPage() {
  const { t } = useTranslation('landing');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  useLandingImagePreload();

  useEffect(() => {
    ensureNamespaces(['landing']);
  }, []);

  useEffect(() => {
    consumeLandingSectionScroll();

    const hash = window.location.hash;
    if (!hash) return;

    window.requestAnimationFrame(() => {
      scrollToLandingSection(hash);
    });
  }, []);

  const openLightbox = (image, alt) => {
    setLightboxImage(image);
    setLightboxAlt(alt);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxAlt('');
  };

  return (
    <LandingPageRoot>
      <LandingHeaderSection />

      <LandingContainer>
        <HeroSection>
          <HeroContent>
            <HeroTitle>
              <span>{t('hero.titleLine1')}</span>
              <span>{t('hero.titleLine2')}</span>
            </HeroTitle>
            <HeroDescription>{t('hero.description')}</HeroDescription>

            <StoreBadgesRow>
              <StoreButtons>
                <StoreBadgeLink
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="App Store"
                >
                  <StoreBadge src={appStoreBadge} alt="App Store" />
                </StoreBadgeLink>
                <StoreBadgeLink
                  href={ANDROID_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Google Play"
                >
                  <StoreBadge src={googlePlayBadge} alt="Google Play" />
                </StoreBadgeLink>
              </StoreButtons>
            </StoreBadgesRow>
          </HeroContent>

          <HeroVisual>
            <HeroImage src={LANDING_HERO_IMAGE} alt="" decoding="async" fetchPriority="high" />
          </HeroVisual>
        </HeroSection>
      </LandingContainer>

      <FeatureCareSection>
        <FeatureCareInner>
          <FeatureCareTitle>{t('featureCare.title')}</FeatureCareTitle>
          <FeatureCareSliderWrap>
            <WelcomeSlider onPrimaryBackground />
          </FeatureCareSliderWrap>
        </FeatureCareInner>
      </FeatureCareSection>

      <FeatureScreensSection id="features">
        <LandingContainer>
          <FeatureScreensTitle>{t('featureScreens.title')}</FeatureScreensTitle>
          <FeatureScreensGrid>
            {FEATURE_SCREENS.map(({ key, image }) => (
              <FeatureScreenCard key={key}>
                <FeatureScreenImageWrap>
                  <FeatureScreenImageButton
                    type="button"
                    onClick={() => openLightbox(image, t(`featureScreens.${key}`))}
                    aria-label={t(`featureScreens.${key}`)}
                  >
                    <FeatureScreenImage src={image} alt="" loading="lazy" decoding="async" />
                  </FeatureScreenImageButton>
                </FeatureScreenImageWrap>
                <FeatureScreenCaption>
                  {t(`featureScreens.${key}`)}
                </FeatureScreenCaption>
              </FeatureScreenCard>
            ))}
          </FeatureScreensGrid>
        </LandingContainer>
      </FeatureScreensSection>

      <ExportRecordsSection>
        <LandingContainer>
          <ExportRecordsRow>
            <ExportRecordsContent>
              <ExportRecordsTitle>{t('exportRecords.title')}</ExportRecordsTitle>
              <ExportRecordsSubtitle>{t('exportRecords.subtitle')}</ExportRecordsSubtitle>
            </ExportRecordsContent>

            <ExportRecordsVisual>
              <ExportRecordsImage src={LANDING_EXPORT_IMAGE} alt="" loading="lazy" decoding="async" />
            </ExportRecordsVisual>
          </ExportRecordsRow>
        </LandingContainer>
      </ExportRecordsSection>

      <LandingFaqSection />

      <LandingContactsSection />

      <LandingFooterSection />

      <LandingFeatureScreenLightbox
        image={lightboxImage}
        alt={lightboxAlt}
        onClose={closeLightbox}
      />
    </LandingPageRoot>
  );
}
